import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { AlertTriangle, Search, Download, ChevronDown, ChevronRight, Bot as BotIcon, Clock, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { realTimeTradingPredictionService, type Bot, type DuplicateTimestamp } from '@/services/RealTimeTradingPredictionService';
import { formatDistanceToNow } from 'date-fns';

export const DuplicateTimestampChecker: React.FC = () => {
  const [linkedBots, setLinkedBots] = useState<Bot[]>([]);
  const [selectedBotId, setSelectedBotId] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [duplicates, setDuplicates] = useState<DuplicateTimestamp[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadLinkedBots();
  }, []);

  const loadLinkedBots = async () => {
    try {
      const bots = await realTimeTradingPredictionService.getBotsWithLinkedFiles();
      setLinkedBots(bots);
      
      if (bots.length > 0 && !selectedBotId) {
        setSelectedBotId(bots[0].id);
      }
    } catch (error) {
      console.error('Error loading linked bots:', error);
      toast.error('Failed to load linked bots');
    }
  };

  const handleScan = async () => {
    if (!selectedBotId) {
      toast.error('Please select a bot first');
      return;
    }

    setIsLoading(true);
    try {
      const fromDateTime = fromDate ? new Date(fromDate) : undefined;
      const toDateTime = toDate ? new Date(toDate) : undefined;

      const duplicateData = await realTimeTradingPredictionService.getDuplicateTimestamps(
        selectedBotId,
        fromDateTime,
        toDateTime
      );
      
      setDuplicates(duplicateData);
      
      if (duplicateData.length === 0) {
        toast.success('No duplicate timestamps found!');
      } else {
        toast.warning(`Found ${duplicateData.length} duplicate timestamp groups`);
      }
    } catch (error) {
      console.error('Error scanning for duplicates:', error);
      toast.error('Failed to scan for duplicates');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRowExpansion = (timestamp: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(timestamp)) {
      newExpandedRows.delete(timestamp);
    } else {
      newExpandedRows.add(timestamp);
    }
    setExpandedRows(newExpandedRows);
  };

  const exportToCSV = () => {
    if (duplicates.length === 0) {
      toast.error('No data to export');
      return;
    }

    const csvData = duplicates.map(dup => ({
      Timestamp: dup.timestamp.toISOString(),
      'Duplicate Count': dup.duplicateCount,
      'Sample Action': dup.sampleAction,
      'Sample Instrument': dup.sampleInstrument,
      'Sample Result': dup.samplePredictedResult || 'N/A',
      'Sample Win': dup.sampleWin ? 'WIN' : 'LOSS',
      'Trade IDs': dup.tradeIds.join(', ')
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `duplicate-timestamps-${selectedBotId}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Data exported to CSV');
  };

  const selectedBot = linkedBots.find(bot => bot.id === selectedBotId);
  const totalDuplicateRecords = duplicates.reduce((sum, dup) => sum + dup.duplicateCount, 0);
  const affectedPercentage = duplicates.length > 0 ? 
    ((totalDuplicateRecords / (duplicates.length + totalDuplicateRecords)) * 100).toFixed(1) : '0';

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            <div>
              <CardTitle>Duplicate Timestamp Checker</CardTitle>
              <CardDescription>
                Scan for duplicate timestamps in realtime trading data
              </CardDescription>
            </div>
          </div>
          {duplicates.length > 0 && (
            <Button variant="outline" size="sm" onClick={exportToCSV}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {linkedBots.length === 0 ? (
          <div className="text-center py-8">
            <BotIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-lg font-medium mb-2">No linked bots found</p>
            <p className="text-sm text-muted-foreground">
              Link trading bots to CSV files to check for duplicate timestamps
            </p>
          </div>
        ) : (
          <>
            {/* Controls */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Select Bot</Label>
                <Select value={selectedBotId} onValueChange={setSelectedBotId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a bot..." />
                  </SelectTrigger>
                  <SelectContent>
                    {linkedBots.map((bot) => (
                      <SelectItem key={bot.id} value={bot.id}>
                        <div className="flex items-center gap-2">
                          <BotIcon className="h-4 w-4" />
                          {bot.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>From Date (Optional)</Label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>To Date (Optional)</Label>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label className="invisible">Action</Label>
                <Button 
                  onClick={handleScan} 
                  disabled={isLoading || !selectedBotId}
                  className="w-full"
                >
                  <Search className="h-4 w-4 mr-2" />
                  {isLoading ? 'Scanning...' : 'Scan'}
                </Button>
              </div>
            </div>

            {/* Summary Cards */}
            {duplicates.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-2xl font-bold">{duplicates.length}</p>
                        <p className="text-xs text-muted-foreground">Duplicate Groups</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-warning" />
                      <div>
                        <p className="text-2xl font-bold">{totalDuplicateRecords}</p>
                        <p className="text-xs text-muted-foreground">Total Duplicate Records</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-destructive" />
                      <div>
                        <p className="text-2xl font-bold">{affectedPercentage}%</p>
                        <p className="text-xs text-muted-foreground">Affected Data</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Results Table */}
            {duplicates.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Duplicate Timestamp Groups</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]"></TableHead>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>Count</TableHead>
                      <TableHead>Sample Action</TableHead>
                      <TableHead>Sample Instrument</TableHead>
                      <TableHead>Sample Result</TableHead>
                      <TableHead>Time Ago</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {duplicates.map((duplicate) => {
                      const timestampKey = duplicate.timestamp.toISOString();
                      const isExpanded = expandedRows.has(timestampKey);
                      
                      return (
                        <React.Fragment key={timestampKey}>
                          <TableRow>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleRowExpansion(timestampKey)}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </Button>
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {duplicate.timestamp.toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Badge variant="destructive">
                                {duplicate.duplicateCount}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {duplicate.sampleAction}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium">
                              {duplicate.sampleInstrument}
                            </TableCell>
                            <TableCell>
                              {duplicate.samplePredictedResult ? (
                                <Badge 
                                  className={duplicate.samplePredictedResult === 'WIN' ? 
                                    'bg-blue-500/10 text-blue-700 border-blue-200' :
                                    'bg-orange-500/10 text-orange-700 border-orange-200'
                                  }
                                >
                                  {duplicate.samplePredictedResult}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-muted-foreground">
                                  No Prediction
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDistanceToNow(duplicate.timestamp, { addSuffix: true })}
                            </TableCell>
                          </TableRow>
                          
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={7} className="bg-muted/30">
                                <div className="py-4">
                                  <h4 className="font-medium mb-2">Duplicate Trade IDs:</h4>
                                  <div className="flex flex-wrap gap-2">
                                    {duplicate.tradeIds.map((tradeId, index) => (
                                      <Badge 
                                        key={tradeId} 
                                        variant="secondary"
                                        className="font-mono text-xs"
                                      >
                                        {index === 0 ? '🥇 ' : ''}
                                        {tradeId}
                                      </Badge>
                                    ))}
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-2">
                                    🥇 = First/original record (by creation time)
                                  </p>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* No Results */}
            {!isLoading && duplicates.length === 0 && selectedBotId && (
              <div className="text-center py-8">
                <Clock className="h-12 w-12 mx-auto mb-4 text-green-500 opacity-50" />
                <p className="text-lg font-medium mb-2">No duplicates found</p>
                <p className="text-sm text-muted-foreground">
                  All timestamps are unique for {selectedBot?.name}
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};